import { useEffect, useState } from "react";
import api from "../lib/api";
import { displayName } from "../lib/anagConfig";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { EuroDotInput } from "./EuroDotInput";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Pencil, Trash2, Coins, ChevronsUpDown, Loader2 } from "lucide-react";
import { formatNumIt } from "../lib/format";

// Lista unica "tipo compenso" allineata alla scheda segnalatore.
export const TIPO_OPTS = [["fisso", "Euro fisso"], ["percentuale", "Percentuale"]];
export const BASE_OPTS = ["sugli onorari", "sul risarcimento", "sul danno", "altro"];
const TIPO_LABEL = { fisso: "Euro fisso", percentuale: "Percentuale" };
const LBL = "text-slate-300 text-xs uppercase tracking-wide";
const eur = (n) => `${formatNumIt(Number(String(n ?? "").replace(",", ".")) || 0)} €`;
const uid = () => Math.random().toString(36).slice(2, 10);
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;

function SoggettoSelect({ value, onChange, testId }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/anagrafiche/segnalatori").then((r) => r.data).catch(() => []),
      api.get("/anagrafiche/agenzie").then((r) => r.data).catch(() => []),
    ]).then(([s, a]) => setOpts([...(s || []), ...(a || [])])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className="h-9 w-full flex items-center justify-between rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm text-slate-100 hover:border-slate-600">
        <span className={value ? "text-slate-100" : "text-slate-500"}>{value || "Seleziona soggetto"}</span>
        <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-56 overflow-y-auto" data-testid={`${testId}-list`}>
          {loading ? (
            <div className="px-3 py-3 text-center"><Loader2 className="h-4 w-4 animate-spin text-amber-500 inline" /></div>
          ) : opts.length === 0 ? (
            <div className="px-3 py-3 text-center text-sm text-slate-500">Nessun segnalatore/agenzia in anagrafica</div>
          ) : opts.map((o) => (
            <button key={o.id} type="button" onClick={() => { onChange(displayName(o), o); setOpen(false); }} data-testid={`${testId}-opt`}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60 border-b border-slate-800 last:border-0">{displayName(o)}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function ParcellaDialog({ open, initial, defaults, quietanza, onClose, onSave }) {
  const [f, setF] = useState({ soggetto: "", tipo: "fisso", importo: "", percentuale: "", base: "", totale: "" });
  useEffect(() => {
    if (open) setF({ soggetto: "", tipo: "fisso", importo: "", percentuale: "", base: "", totale: "", ...(defaults || {}), ...(initial || {}) });
  }, [open, initial, defaults]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Selezione soggetto: precompila tipo/percentuale/base (o importo) dagli accordi
  // salvati nella scheda del segnalatore. Se non ha accordi, i campi restano vuoti.
  const onSoggetto = (name, rec) => {
    const tipo = rec?.compenso_tipo;
    const perc = rec?.compenso_percentuale;
    const base = rec?.compenso_base;
    const imp = rec?.compenso_importo;
    const hasAccordo = (tipo === "percentuale" && (perc || base)) || !!imp;
    setF((s) => ({
      ...s,
      soggetto: name,
      tipo: hasAccordo ? (tipo || "fisso") : "fisso",
      percentuale: hasAccordo && tipo === "percentuale" ? (perc ?? "") : "",
      base: hasAccordo && tipo === "percentuale" ? (base || "") : "",
      importo: hasAccordo && tipo !== "percentuale" ? (imp ?? "") : "",
    }));
  };

  const qz = quietanza || {};
  const baseAmount = f.base === "sugli onorari" ? num(qz.onorari)
    : (f.base === "sul risarcimento" || f.base === "sul danno") ? num(qz.sorte) : 0;
  const computable = f.tipo === "percentuale" && f.base && f.base !== "altro" && num(f.percentuale) > 0;
  const autoTotale = computable ? baseAmount * num(f.percentuale) / 100 : null;
  const finalTotale = f.tipo === "fisso" ? num(f.importo) : (autoTotale != null ? autoTotale : num(f.totale));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="parcella-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Coins className="h-5 w-5 text-amber-400" /> {initial ? "Modifica parcella" : "Nuova parcella"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className={LBL}>Soggetto</Label>
            <div className="mt-1.5"><SoggettoSelect value={f.soggetto} onChange={onSoggetto} testId="parcella-soggetto" /></div>
          </div>
          <div>
            <Label className={LBL}>Parcella (tipo compenso)</Label>
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="parcella-tipo" className="mt-1.5 h-9 bg-slate-900/60 border-slate-700 text-slate-100"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                {TIPO_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {f.tipo === "fisso" ? (
            <div>
              <Label className={LBL}>Importo (€)</Label>
              <div className="mt-1.5"><EuroDotInput value={f.importo} onChange={(v) => set("importo", v)} testId="parcella-importo" className="h-9 bg-slate-900/60 border-slate-700" /></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={LBL}>Percentuale (%)</Label>
                  <Input type="number" step="0.01" value={f.percentuale} onChange={(e) => set("percentuale", e.target.value)} data-testid="parcella-percentuale" className="mt-1.5 h-9 bg-slate-900/60 border-slate-700 font-mono" />
                </div>
                <div>
                  <Label className={LBL}>Base di calcolo</Label>
                  <Select value={f.base || undefined} onValueChange={(v) => set("base", v)}>
                    <SelectTrigger data-testid="parcella-base" className="mt-1.5 h-9 bg-slate-900/60 border-slate-700 text-slate-100"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                      {BASE_OPTS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {computable && (
                <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2 text-xs text-slate-400" data-testid="parcella-base-info">
                  Base {f.base}: <span className="font-mono text-slate-300">{eur(baseAmount)}</span> (dall'ultima quietanza) × {num(f.percentuale)}%
                </div>
              )}
            </>
          )}

          <div>
            <Label className={LBL}>Totale (€)</Label>
            {f.tipo === "percentuale" && computable ? (
              <div className="mt-1.5"><EuroDotInput readOnly value={finalTotale} testId="parcella-totale" className="h-9 bg-slate-800/40 border-slate-700 text-teal-300" /></div>
            ) : (
              <div className="mt-1.5"><EuroDotInput value={f.tipo === "fisso" ? f.importo : f.totale} onChange={(v) => set(f.tipo === "fisso" ? "importo" : "totale", v)} testId="parcella-totale" className="h-9 bg-slate-900/60 border-slate-700" /></div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="parcella-cancel" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
          <Button type="button" onClick={() => onSave({ ...f, totale: finalTotale, id: initial?.id || uid() })} data-testid="parcella-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Salva parcella</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Sezione "Gestione Parcelle Segnalatori" collegata al segnalatore del cliente.
export default function ParcelleSegnalatore({ abilitata, parcelle = [], segnalatore, segCompenso, quietanza, onAbilitataChange, onParcelleChange }) {
  const [dlg, setDlg] = useState(null); // { editing: parcella|null }
  const save = (p) => {
    const arr = [...parcelle];
    const i = arr.findIndex((x) => x.id === p.id);
    if (i >= 0) arr[i] = p; else arr.push(p);
    onParcelleChange(arr);
    setDlg(null);
  };
  const del = (id) => onParcelleChange(parcelle.filter((x) => x.id !== id));

  const defaults = {
    soggetto: segnalatore || "",
    tipo: segCompenso?.tipo || "fisso",
    percentuale: segCompenso?.perc ?? "",
    base: segCompenso?.base || "",
    importo: segCompenso?.importo ?? "",
  };

  return (
    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="gestione-parcelle">
      <h4 className="font-head text-sm uppercase tracking-wide text-amber-300 flex items-center gap-2 mb-3"><Coins className="h-4 w-4" /> Gestione Parcelle Segnalatori{segnalatore ? <span className="text-slate-400 normal-case font-normal">· {segnalatore}</span> : null}</h4>
      <label className="flex items-center gap-2 cursor-pointer mb-3">
        <Checkbox checked={!!abilitata} onCheckedChange={(v) => onAbilitataChange(!!v)} data-testid="provvigione-abilitata" />
        <span className="text-sm text-slate-200">Abilitato alla provvigione</span>
      </label>
      {abilitata && (() => {
        const pctOk = segCompenso && (segCompenso.tipo || "").toLowerCase() === "percentuale" && Number(segCompenso.perc) > 0 && !!segCompenso.base;
        const fixOk = segCompenso && (segCompenso.tipo || "fisso").toLowerCase() === "fisso" && Number(segCompenso.importo) > 0;
        if (!pctOk && !fixOk && parcelle.length > 0) return null;
        return (
        <div className="mb-3 rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2" data-testid="provvigione-config-readonly">
          {pctOk ? (
            <div className="text-sm text-slate-300">Percentuale provvigione: <span className="font-mono font-semibold text-amber-300" data-testid="provvigione-percentuale-readonly">{segCompenso.perc}%</span> · <span className="text-slate-400">{segCompenso.base}</span></div>
          ) : fixOk ? (
            <div className="text-sm text-slate-300">Compenso fisso: <span className="font-mono font-semibold text-amber-300">{eur(segCompenso.importo)}</span></div>
          ) : (
            <div className="text-sm text-slate-400" data-testid="provvigione-nessun-compenso">Nessun compenso impostato per il calcolo automatico.</div>
          )}
          <div className="text-[11px] text-slate-500 mt-0.5">Modificabile nella scheda del segnalatore (Anagrafiche → Segnalatore → Compenso).</div>
        </div>
        );
      })()}

      <div className="rounded-md border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-[10px] uppercase tracking-wider font-mono bg-slate-900/50">
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">%</th>
              <th className="px-3 py-2">Base</th>
              <th className="px-3 py-2">Totale</th>
              <th className="px-3 py-2 w-20 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {parcelle.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500 text-xs" data-testid="parcelle-empty">Nessuna parcella. Usa "Nuova parcella".</td></tr>
            ) : parcelle.map((p) => (
              <tr key={p.id} data-testid={`parcella-row-${p.id}`} className="border-t border-slate-800">
                <td className="px-3 py-2 text-slate-100">
                  <div className="font-medium">{TIPO_LABEL[p.tipo] || p.tipo}</div>
                  {p.soggetto && <div className="text-[11px] text-slate-500">{p.soggetto}</div>}
                </td>
                <td className="px-3 py-2 text-slate-300 font-mono">{p.tipo === "percentuale" && p.percentuale ? `${p.percentuale}%` : "—"}</td>
                <td className="px-3 py-2 text-slate-300 text-xs">{p.tipo === "percentuale" ? (p.base || "—") : "—"}</td>
                <td className="px-3 py-2 text-slate-300 font-mono">{eur(p.totale)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => setDlg({ editing: p })} data-testid={`parcella-edit-${p.id}`} title="Modifica" className="p-1 rounded hover:bg-slate-700/50 text-slate-300 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => del(p.id)} data-testid={`parcella-del-${p.id}`} title="Elimina" className="p-1 rounded hover:bg-slate-700/50 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="button" onClick={() => setDlg({ editing: null })} data-testid="parcella-nuova" className="mt-3 h-9 bg-slate-700 hover:bg-slate-600 text-white">
        <Plus className="h-4 w-4 mr-1" /> Nuova parcella
      </Button>

      {dlg && <ParcellaDialog open initial={dlg.editing} defaults={defaults} quietanza={quietanza} onClose={() => setDlg(null)} onSave={save} />}
    </div>
  );
}
